import { useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useUnit } from 'effector-react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { useAddStrategy } from './queries';
import { schema } from './schema';
import { Option, FormFields, AddStrategyRequest } from './types';
import { ControlledCheckboxField } from '../../../../../../components/FormControlsV2/ControlledCheckboxField';
import { ControlledSelectField } from '../../../../../../components/FormControlsV2/ControlledSelectField';
import { ControlledTextEditorField } from '../../../../../../components/FormControlsV2/ControlledTextEditorField';
import { ControlledTextField } from '../../../../../../components/FormControlsV2/ControlledTextField';
import { FormProvider } from '../../../../../../components/FormControlsV2/FormProvider';
import { recalcModes } from '../../../../../../const/strategyRefs';
import { strategyInfoRef$ } from '../../../../../../effector/aggregateStrategyInfoRefs';
import { currencyInfo$ } from '../../../../../../effector/currencies';
import { Strategies$ } from '../../../../../../effector/strategies';
import { User$ } from '../../../../../../effector/user';
import { useAllUsers } from '../../../../../../hooks/useUsers';
import { RecalcMode } from '../../../../../../types/aggregateStrategy';
import { Currency, StrategyDuration } from '../../../../../../types/aggregateStrategyInfoRef';
import { Profile } from '../../../../../../types/profile';
import { parseApiError, isErrorResponse } from '../../../../../../utils/apiErrors';

interface Props {
  onUpdate: () => void;
}

const twoColumnFieldsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 2,
  '& .MuiFormControl-root': {
    margin: 0,
  },
  '& .MuiTextField-root': {
    margin: 0,
  },
};

export function AddStrategyForm({ onUpdate }: Props) {
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const userStore = useUnit(User$);
  const strategiesStore = useUnit(Strategies$);
  const infoStore = useUnit(strategyInfoRef$);
  const currencyStore = useUnit(currencyInfo$);
  const { authors } = useAllUsers();

  const { mutateAsync, isPending: isLoading } = useAddStrategy();

  const users: Option[] = useMemo(() => authors.map((u) => ({ value: String(u.id), label: u.name })), [authors]);

  const durations: Option[] = useMemo(
    () => infoStore.data.durations.map((d: StrategyDuration) => ({ value: String(d.id), label: d.name })),
    [infoStore.data.durations],
  );

  const profiles: Option[] = useMemo(
    () => infoStore.data.profiles.map((p: Profile) => ({ value: String(p.id), label: p.name })),
    [infoStore.data.profiles],
  );

  const indexes: Option[] = useMemo(
    () => infoStore.data.indexes.map((i: { id: number; name: string }) => ({ value: String(i.id), label: i.name })),
    [infoStore.data.indexes],
  );

  const currencies: Option[] = useMemo(
    () => currencyStore.map((c: Currency) => ({ value: c.code, label: c.name })),
    [currencyStore],
  );

  const recalcModeOptions: Option[] = useMemo(
    () =>
      recalcModes
        .filter((m: { id: RecalcMode }) => m.id === RecalcMode.CurrentPriceRecalc)
        .map((m: { id: RecalcMode; name: string }) => ({ value: String(m.id), label: m.name })),
    [],
  );

  const parentStrategies: Option[] = useMemo(
    () => strategiesStore.filter((s) => s.parentStrategy === null).map((s) => ({ value: String(s.id), label: s.name })),
    [strategiesStore],
  );

  const defaultValues: FormFields = {
    title: '',
    tagline: null,
    author: '',
    managerIds: [],
    isBlogger: false,
    welcomeLetter: '',
    subscription: '',
    profile: null,
    currency: currencies[0]?.value || '',
    minSumma: 1000,
    recommendedAmount: null,
    capacity: null,
    estimatedProfit: null,
    estimatedTradesPerWeek: null,
    index: '',
    period: '',
    maxInstrumentWeight: 50,
    maxSignalWeight: 50,
    leverage: 0,
    recalcMode: recalcModeOptions[0]?.value || '',
    hasParent: false,
    parent: null,
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormFields>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const hasParent = watch('hasParent');

  const onSubmit = async (values: FormFields) => {
    const data: AddStrategyRequest = {
      name: values.title,
      tagline: values.tagline || '',
      managerId: Number(values.author),
      managerName: users.find((u) => u.value === values.author)?.label || '',
      managerIds: values.managerIds?.map((id) => Number(id)) || [],
      creatorId: userStore ? userStore.id : 0,
      creationDate: new Date().toDateString(),
      goal: values.welcomeLetter,
      chartComment: values.subscription,
      currency: values.currency,
      subscriptionThreshold: values.minSumma,
      recommendedAmount: values.recommendedAmount ?? undefined,
      capacity: values.capacity ?? undefined,
      estimatedProfit: values.estimatedProfit ?? undefined,
      estimatedTradesPerWeek: values.estimatedTradesPerWeek ?? undefined,
      indexId: values.index ? Number(values.index) : undefined,
      durationId: Number(values.period),
      maxInstrumentWeight: values.maxInstrumentWeight,
      maxSignalWeight: values.maxSignalWeight ?? undefined,
      leverage: values.leverage,
      recalcMode: Number(values.recalcMode) as RecalcMode,
      parentId: values.hasParent && values.parent ? values.parent : undefined,
      securities: '[]',
      active: true,
      rating: 0,
      estimatedDrawdown: 0,
      actualProfit: 0,
      maxIndustryWeight: 0,
      minInvestProfileId: values.profile ? Number(values.profile) : undefined,
      open: false,
      order: 1000,
      price: '',
      showFullPortfolio: false,
      tradesFrequency: '',
      isRestricted: false,
      isBlogger: values.isBlogger,
      iis: true,
    };

    try {
      const result = await mutateAsync(data);

      if (isErrorResponse(result)) {
        setError(parseApiError(result));
        return;
      }

      onUpdate();
      enqueueSnackbar('Стратегия добавлена');
    } catch (err) {
      setError(parseApiError(err));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
      <FormProvider control={control} errors={errors}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Описание
        </Typography>
        <ControlledTextField name="title" label="Название" />
        <ControlledTextField name="tagline" label="Слоган" />
        <ControlledSelectField name="author" label="Автор" options={users} />
        <ControlledSelectField name="managerIds" label="Управляющие" options={users} multiple />
        <ControlledCheckboxField name="isBlogger" label="Стратегия блогера" />
        <ControlledTextEditorField name="welcomeLetter" label="Текст для Welcome letter" />
        <ControlledTextField name="subscription" label="Подпись под графиком" />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Характеристики
          </Typography>

          <Box sx={twoColumnFieldsGridSx}>
            <ControlledSelectField name="profile" label="Инвестпрофиль" options={profiles} />
            <ControlledSelectField name="currency" label="Валюта" options={currencies} />
          </Box>

          <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
            <ControlledTextField type="number" name="minSumma" label="Минимальная сумма" />
            <ControlledTextField type="number" name="recommendedAmount" label="Рекомендованная сумма" />
            <ControlledTextField type="number" name="capacity" label="Ёмкость" />
            <ControlledTextField type="number" name="estimatedProfit" label="Прогнозная доходность" />
          </Box>

          <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
            <ControlledTextField type="number" name="estimatedTradesPerWeek" label="Прогнозная частота сделок" />
            <ControlledSelectField name="period" label="Срок" options={durations} />
            <ControlledSelectField name="index" label="Бенчмарк" options={indexes} />
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Параметры
          </Typography>

          <Box sx={twoColumnFieldsGridSx}>
            <ControlledTextField type="number" name="maxInstrumentWeight" label="Максимальная доля инструмента, %" />
            <ControlledTextField
              type="number"
              name="maxSignalWeight"
              label="Максимальная доля инструмента в сигнале, %"
            />
          </Box>

          <Box sx={{ ...twoColumnFieldsGridSx, mt: 2 }}>
            <ControlledTextField type="number" name="leverage" label="Используемое плечо" />
            <ControlledSelectField name="recalcMode" label="Пересчет весов" options={recalcModeOptions} />
          </Box>

          <ControlledCheckboxField name="hasParent" label="Дочерняя" />
          <ControlledSelectField
            name="parent"
            label="Родительская стратегия"
            options={parentStrategies}
            disabled={!hasParent}
          />
        </Box>
      </FormProvider>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={!isDirty}
        fullWidth
        sx={{ mt: 2 }}
        startIcon={isLoading && <CircularProgress size={20} />}
      >
        Добавить
      </Button>
    </form>
  );
}
